import type { Activity } from "./schemas";

export type RouteProvider = "wikiloc" | "strava" | "web";

/** What can be read from a route link without fetching it. */
export type RouteLink = {
  provider: RouteProvider;
  url: string;
  id: string | null;
  title: string | null;
  activity: Activity | null;
};

/** A route link enriched by the server (Strava public activity pages). */
export type RoutePreview = RouteLink & {
  date: string | null;
  startTime: string | null;
  imageUrl: string | null;
};

// Wikiloc puts the activity in the first path segment, localised ("rutas-senderismo", "hiking-trails"…).
// Order matters: "ski-mountaineering" is skiing, not mountaineering.
const WIKILOC_ACTIVITY: [RegExp, Activity][] = [
  [/esqui|ski/, "esqui_montana"],
  [/raquet|snowshoe/, "raquetas"],
  [/ferrata/, "via_ferrata"],
  [/escalada|climbing|escalade|arrampicata|klettern/, "escalada"],
  [/alpinis|mountaineering|bergsteigen/, "alpinismo"],
  [/senderismo|hiking|randonnee|caminhada|escursionismo|wandern|trekking|excursionisme/, "senderismo"],
];

const STRAVA_SPORT: Record<string, Activity> = {
  Hike: "senderismo",
  Walk: "senderismo",
  BackcountrySki: "esqui_montana",
  AlpineSki: "esqui_montana",
  NordicSki: "esqui_montana",
  Snowshoe: "raquetas",
  RockClimbing: "escalada",
};

export const stravaActivity = (sport: string | null | undefined): Activity | null => (sport ? (STRAVA_SPORT[sport] ?? null) : null);

const isHost = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);

/** "aneto-desde-la-besurta" → "Aneto desde la besurta" (Wikiloc slugs drop accents and case). */
function slugTitle(slug: string): string | null {
  const s = decodeURIComponent(slug).replace(/[-_]+/g, " ").trim();
  return s ? s[0]!.toUpperCase() + s.slice(1) : null;
}

export function parseRouteLink(raw: string | null | undefined): RouteLink | null {
  if (!raw) return null;
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const host = u.hostname.toLowerCase();
  const parts = u.pathname.split("/").filter(Boolean);

  if (isHost(host, "wikiloc.com")) {
    // /{activity}-trails/{slug}-{id}  ·  /wikiloc/view.do?id={id}
    const last = parts.at(-1) ?? "";
    const m = /^(.*?)-?(\d{4,})$/.exec(last);
    const id = m?.[2] ?? u.searchParams.get("id");
    const category = parts.length >= 2 ? parts[0]!.toLowerCase() : "";
    return {
      provider: "wikiloc",
      url: `${u.origin}${u.pathname}${id && !m ? `?id=${id}` : ""}`,
      id: id ?? null,
      title: m?.[1] ? slugTitle(m[1]) : null,
      activity: WIKILOC_ACTIVITY.find(([re]) => re.test(category))?.[1] ?? null,
    };
  }

  if (isHost(host, "strava.com") || host === "strava.app.link") {
    const i = parts.findIndex((p) => p === "activities" || p === "routes");
    const id = i >= 0 && /^\d+$/.test(parts[i + 1] ?? "") ? parts[i + 1]! : null;
    return {
      provider: "strava",
      url: id ? `https://www.strava.com/${parts[i]}/${id}` : u.href,
      id,
      title: null,
      activity: null,
    };
  }

  return { provider: "web", url: u.href, id: null, title: null, activity: null };
}
